import type { FocusPhase } from './foco-estado';

/**
 * EL AVISO DE QUE TERMINÓ EL FOCO (spec 2026-10-03, «Fin del foco»). Lo envía el servidor por push
 * a la hora de fin, aunque la app esté cerrada. Aquí se decide cuándo programarlo
 * y cuándo cancelarlo, y se le avisa al servidor; sin cuenta no se hace nada y el
 * foco funciona igual.
 */

export type FotoFoco = {
  phase: FocusPhase;
  sesionId: string | null;
  endsAt: number | null;
  remainingMs: number;
  plannedMinutes: number;
  label: string;
  habitId: string | null;
  tipo: 'foco' | 'descanso';
};

export type AccionAviso =
  | {
      tipo: 'programar';
      sesionId: string;
      restanteMs: number;
      minutos: number;
      etiqueta: string | null;
      descanso: boolean;
    }
  | { tipo: 'cancelar'; sesionId: string }
  | null;

/** Lo que aún faltaba al terminar para contarlo como «antes de tiempo». */
const MARGEN_MS = 2000;

export function accionDeAviso(
  antes: { phase: FocusPhase; sesionId: string | null },
  ahora: FotoFoco,
  ahoraMs: number,
): AccionAviso {
  if (ahora.phase === 'running' && antes.phase !== 'running') {
    if (!ahora.sesionId || ahora.endsAt === null) return null;
    return programar(ahora, ahora.sesionId, ahoraMs);
  }
  const abierta = antes.phase === 'running' || antes.phase === 'paused';
  if (abierta && antes.sesionId && (ahora.phase === 'paused' || ahora.phase === 'idle')) {
    return { tipo: 'cancelar', sesionId: antes.sesionId };
  }
  // Terminar a mano antes de la hora: el aviso programado ya no es verdad.
  if (ahora.phase === 'done' && ahora.sesionId && ahora.remainingMs > MARGEN_MS) {
    return { tipo: 'cancelar', sesionId: ahora.sesionId };
  }
  return null;
}

/** Lo mismo que al empezar, para reintentarlo si la primera vez no había red. */
export function reprogramar(ahora: FotoFoco, ahoraMs: number): AccionAviso {
  if (ahora.phase !== 'running' || !ahora.sesionId || ahora.endsAt === null) return null;
  return programar(ahora, ahora.sesionId, ahoraMs);
}

function programar(ahora: FotoFoco, sesionId: string, ahoraMs: number): AccionAviso {
  return {
    tipo: 'programar',
    sesionId,
    restanteMs: Math.max(0, (ahora.endsAt ?? ahoraMs) - ahoraMs),
    minutos: ahora.plannedMinutes,
    // Un foco libre se llama «Foco»: en el aviso no diría nada.
    etiqueta: ahora.habitId ? ahora.label || null : null,
    descanso: ahora.tipo === 'descanso',
  };
}

/** Fuego y olvido: sin red o sin sesión no pasa nada; se reintenta al volver. */
export function enviarAccion(accion: AccionAviso): void {
  if (!accion) return;
  void (async () => {
    try {
      const { cuentaVinculada } = await import('../store/sync');
      if (!(await cuentaVinculada())) return;
      const { avisoDeFoco } = await import('../store/push');
      await avisoDeFoco(
        accion.tipo === 'cancelar'
          ? { sesionId: accion.sesionId, restanteMs: null }
          : {
              sesionId: accion.sesionId,
              restanteMs: accion.restanteMs,
              minutos: accion.minutos,
              etiqueta: accion.etiqueta,
              descanso: accion.descanso,
            },
      );
    } catch {
      // El aviso es una ayuda; el foco y su registro no dependen de él.
    }
  })();
}
